// Unified Export System - Using dom-to-image-more for better accuracy
import domtoimage from 'dom-to-image-more';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, listAll, deleteObject } from 'firebase/storage';
import { getFirestore, doc, updateDoc } from 'firebase/firestore';

export const exportUnifiedCanvas = async (canvasId) => {
  try {
    console.log('🎯 UNIFIED EXPORT (dom-to-image) - Starting export...');
    
    const canvasElement = document.getElementById(canvasId);
    if (!canvasElement) {
      throw new Error(`Canvas element with ID "${canvasId}" not found`);
    }

    // Wait for fonts to be ready and a small delay to let layout settle
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    await new Promise(resolve => setTimeout(resolve, 160));

    console.log('📸 Capturing canvas with dom-to-image...');

    // Add a short-lived class so we can apply export-only CSS to the cloned node
    // (this helps hide any transient overlays / pseudo-elements that sometimes
    // get cloned and render as faint boxes in the export)
    canvasElement.classList.add('exporting');

  // Wait for all images inside the canvas to be loaded (or timeout) to avoid blank placeholders
    const waitForImages = (root, timeout = 3000) => {
      const imgs = Array.from(root.querySelectorAll('img'));
      if (imgs.length === 0) return Promise.resolve();
      return new Promise((resolve) => {
        let remaining = imgs.length;
        const checkDone = () => {
          remaining -= 1;
          if (remaining <= 0) resolve();
        };
        imgs.forEach(img => {
          if (img.complete && img.naturalWidth > 0) {
            checkDone();
          } else {
            const onLoad = () => { img.removeEventListener('load', onLoad); img.removeEventListener('error', onError); checkDone(); };
            const onError = () => { img.removeEventListener('load', onLoad); img.removeEventListener('error', onError); checkDone(); };
            img.addEventListener('load', onLoad);
            img.addEventListener('error', onError);
          }
        });
        // Fallback timeout
        setTimeout(resolve, timeout);
      });
    };

    await waitForImages(canvasElement, 4000);

    // Convert external image srcs to blob URLs so dom-to-image can capture them
    // without being blocked by CORS (Firebase Storage doesn't send CORS headers
    // for browser-initiated requests from dom-to-image's XHR fetch).
    const blobifiedImgs = [];
    const bgImgs = Array.from(canvasElement.querySelectorAll('img:not(.brand-logo)'));
    await Promise.all(bgImgs.map(async (img) => {
      const originalSrc = img.src;
      if (!originalSrc || originalSrc.startsWith('blob:') || originalSrc.startsWith('data:')) return;
      try {
        const resp = await fetch(originalSrc);
        if (resp.ok) {
          const blob = await resp.blob();
          if (blob.size > 0) {
            const blobUrl = URL.createObjectURL(blob);
            img.src = blobUrl;
            blobifiedImgs.push({ img, originalSrc, blobUrl });
          }
        }
      } catch (_) { /* if fetch fails, leave src as-is */ }
    }));

    // Force inline filter on logos so the exported clone renders them white (dom-to-image prefers inline styles)
    const logoImgs = Array.from(canvasElement.querySelectorAll('.brand-logo'));
    const prevFilters = new Map();
    logoImgs.forEach(img => {
      prevFilters.set(img, { filter: img.style.filter || '', webkitFilter: img.style.webkitFilter || '' });
      try {
        img.style.filter = 'brightness(0) saturate(100%) invert(1)';
        img.style.webkitFilter = 'brightness(0) saturate(100%) invert(1)';
      } catch (e) {
        // ignore
      }
    });

  // Compute scaling so we can pass scaled CSS variables into the cloned node.
  // This keeps computed font-sizes and padding identical between preview and export.
  const sourceRect = canvasElement.getBoundingClientRect();
  const scale = 800 / Math.max(sourceRect.width, sourceRect.height);

    // Read CSS custom props from the source element (if present)
    const computed = window.getComputedStyle(canvasElement);
    const headingSize = computed.getPropertyValue('--heading-size') || '';
    const bodySize = computed.getPropertyValue('--body-size') || '';
    const padSize = computed.getPropertyValue('--pad-size') || '';
  const overlayOpacity = computed.getPropertyValue('--overlay-opacity') || '';

    // Helper to scale CSS sizes like '36px' -> scaled px string
    const scaleCssSize = (val) => {
      if (!val) return '';
      const m = val.trim().match(/^([0-9.]+)px$/);
      if (!m) return val; // leave non-px values alone
      const n = parseFloat(m[1]);
      // preserve fractional scaling to avoid sub-pixel rounding shifts
      return `${(n * scale).toFixed(2)}px`;
    };

  const exportStyleVars = {};
  if (headingSize) exportStyleVars['--heading-size'] = scaleCssSize(headingSize.trim());
  if (bodySize) exportStyleVars['--body-size'] = scaleCssSize(bodySize.trim());
  if (padSize) exportStyleVars['--pad-size'] = scaleCssSize(padSize.trim());
  if (overlayOpacity) exportStyleVars['--overlay-opacity'] = overlayOpacity.trim();

  // Use transform scaling on the clone so the layout/rendering matches the
  // on-screen preview while producing a higher-resolution PNG. Compute the
  // exact pixel width/height for the export and pass those values to dom-to-image
  // to avoid mismatches where the rendered content ends up occupying only
  // a fraction (e.g. top-left quarter) of the output canvas.
  const targetMax = 1600; // maximum pixel size on the largest dimension
  const scaleForExport = targetMax / Math.max(sourceRect.width, sourceRect.height);
  const pixelWidth = Math.round(sourceRect.width * scaleForExport);
  const pixelHeight = Math.round(sourceRect.height * scaleForExport);

  let dataUrl;
  try {
    dataUrl = await domtoimage.toPng(canvasElement, {
      // Output size (pixels) - match the scaled source dimensions
      width: pixelWidth,
      height: pixelHeight,
      style: {
        // Keep the source element at its on-screen CSS size and apply a transform
        // so the cloned node renders at the desired pixel density.
        transform: `scale(${scaleForExport})`,
        transformOrigin: 'top left',
        width: `${sourceRect.width}px`,
        height: `${sourceRect.height}px`,
        // pass scaled CSS custom properties so font sizes/padding match the scaled preview exactly
        ...exportStyleVars
      },
      quality: 0.95,
      filter: (node) => {
        if (node && node.getAttribute && (node.getAttribute('data-debug') || (node.classList && (node.classList.contains('debug-outline') || node.classList.contains('debug-box'))))) {
          return false;
        }
        return true;
      },
      useCORS: true
    });
    } finally {
      // Ensure we always remove the exporting marker even if export fails
      canvasElement.classList.remove('exporting');
      // Restore previous inline filters on logos
      logoImgs.forEach(img => {
        const prev = prevFilters.get(img);
        if (prev) {
          img.style.filter = prev.filter;
          img.style.webkitFilter = prev.webkitFilter;
        }
      });
      // Revoke blob URLs and restore original srcs
      blobifiedImgs.forEach(({ img, originalSrc, blobUrl }) => {
        img.src = originalSrc;
        URL.revokeObjectURL(blobUrl);
      });
    }

    // Create download
    const link = document.createElement('a');
    link.download = `social-media-post-${Date.now()}.png`;
    link.href = dataUrl;
    link.click();
    
    console.log('✅ Export completed successfully with dom-to-image');
    return { success: true, message: 'PNG exported successfully' };
    
  } catch (error) {
    console.error('❌ dom-to-image export error:', error);
    return { success: false, message: `dom-to-image error: ${error.message}` };
  }
};

/**
 * exportAt1080AndUpload
 *
 * Captures the canvas at 1080×1080px and uploads it to Firebase Storage.
 * Updates the Firestore `scheduledPosts/{postId}` doc with the public imageUrl
 * and sets status to 'ready'.
 *
 * @param {string} canvasId        — DOM id of the unified canvas element
 * @param {string} scheduledPostId — Firestore doc ID in `scheduledPosts`
 * @returns {Promise<string>}      — public download URL of the uploaded image
 */
export const exportAt1080AndUpload = async (canvasId, scheduledPostId) => {
  const canvasElement = document.getElementById(canvasId);
  if (!canvasElement) throw new Error(`Canvas element "${canvasId}" not found`);

  // Wait for fonts + tiny layout settle
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  await new Promise(r => setTimeout(r, 160));

  // Measure the canvas at its ACTUAL rendered size — do NOT resize it via CSS vars.
  // Resizing breaks the background image layout inside the canvas.
  // Instead we scale up using dom-to-image's transform, identical to exportUnifiedCanvas.
  const sourceRect = canvasElement.getBoundingClientRect();
  const TARGET = 1080;
  const scaleForExport = TARGET / Math.max(sourceRect.width, sourceRect.height);
  const pixelWidth  = Math.round(sourceRect.width  * scaleForExport);
  const pixelHeight = Math.round(sourceRect.height * scaleForExport);

  canvasElement.classList.add('exporting');

  // Wait for images already in the DOM to fully load
  const waitForImages = (root, timeout = 4000) => {
    const imgs = Array.from(root.querySelectorAll('img'));
    if (imgs.length === 0) return Promise.resolve();
    return new Promise(resolve => {
      let remaining = imgs.length;
      const done = () => { if (--remaining <= 0) resolve(); };
      imgs.forEach(img => {
        if (img.complete && img.naturalWidth > 0) { done(); return; }
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
      setTimeout(resolve, timeout);
    });
  };
  await waitForImages(canvasElement);

  // Blobify external image srcs so dom-to-image can capture them cross-origin
  const blobifiedImgs = [];
  const bgImgs = Array.from(canvasElement.querySelectorAll('img:not(.brand-logo)'));
  await Promise.all(bgImgs.map(async (img) => {
    const originalSrc = img.src;
    if (!originalSrc || originalSrc.startsWith('blob:') || originalSrc.startsWith('data:')) return;
    try {
      const resp = await fetch(originalSrc);
      if (resp.ok) {
        const blob = await resp.blob();
        if (blob.size > 0) {
          const blobUrl = URL.createObjectURL(blob);
          img.src = blobUrl;
          blobifiedImgs.push({ img, originalSrc, blobUrl });
        }
      }
    } catch (_) { /* leave as-is on failure */ }
  }));

  // Inline logo filters so they export white
  const logoImgs = Array.from(canvasElement.querySelectorAll('.brand-logo'));
  const prevFilters = new Map();
  logoImgs.forEach(img => {
    prevFilters.set(img, { filter: img.style.filter, webkitFilter: img.style.webkitFilter });
    img.style.filter = 'brightness(0) saturate(100%) invert(1)';
    img.style.webkitFilter = 'brightness(0) saturate(100%) invert(1)';
  });

  let dataUrl;
  try {
    dataUrl = await domtoimage.toPng(canvasElement, {
      // Output at TARGET resolution, scaled from the actual on-screen size
      width: pixelWidth,
      height: pixelHeight,
      style: {
        transform: `scale(${scaleForExport})`,
        transformOrigin: 'top left',
        width: `${sourceRect.width}px`,
        height: `${sourceRect.height}px`,
      },
      quality: 1.0,
      useCORS: true,
      filter: node => !(node?.getAttribute?.('data-debug') || node?.classList?.contains?.('debug-outline')),
    });
  } finally {
    canvasElement.classList.remove('exporting');
    logoImgs.forEach(img => {
      const prev = prevFilters.get(img);
      if (prev) { img.style.filter = prev.filter; img.style.webkitFilter = prev.webkitFilter; }
    });
    blobifiedImgs.forEach(({ img, originalSrc, blobUrl }) => {
      img.src = originalSrc;
      URL.revokeObjectURL(blobUrl);
    });
  }

  // Convert dataUrl → Blob and upload to Firebase Storage
  const res = await fetch(dataUrl);
  const blob = await res.blob();

  const storage = getStorage();
  const path = `scheduled-posts/${scheduledPostId}-${Date.now()}.png`;
  const ref = storageRef(storage, path);
  await uploadBytes(ref, blob, { contentType: 'image/png' });
  const downloadUrl = await getDownloadURL(ref);

  // Update Firestore
  const db = getFirestore();
  await updateDoc(doc(db, 'scheduledPosts', scheduledPostId), {
    imageUrl: downloadUrl,
    status: 'ready',
  });

  // Trim scheduled-posts/ folder to max 10 files — delete oldest by filename timestamp
  try {
    const folderRef = storageRef(storage, 'scheduled-posts');
    const { items } = await listAll(folderRef);
    if (items.length > 10) {
      // Filenames contain a timestamp: scheduledPostId-{Date.now()}.png — sort ascending
      const sorted = [...items].sort((a, b) => {
        const tsA = parseInt(a.name.replace(/^.*-(\d+)\.png$/, '$1'), 10) || 0;
        const tsB = parseInt(b.name.replace(/^.*-(\d+)\.png$/, '$1'), 10) || 0;
        return tsA - tsB;
      });
      const toDelete = sorted.slice(0, items.length - 10);
      await Promise.all(toDelete.map(item => deleteObject(item).catch(() => {})));
    }
  } catch (_) { /* non-fatal — storage cleanup failure should not block the caller */ }

  return downloadUrl;
};
